import { useState } from "react";
import { updateOccupant } from "../api/webcheckinApi";
import type { WebcheckinDocumentType, WebcheckinOccupant } from "../types";
import { useWizardContext } from "./shared";
import { CountryPicker } from "../components/CountryPicker";
import { PhoneInput } from "../components/PhoneInput";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { cn } from "@/lib/utils";

interface DraftOccupant {
  id: string;
  linkedReservationId: string;
  role: WebcheckinOccupant["role"];
  position: number;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  nationality: string;
  documentType: WebcheckinDocumentType;
  documentNumber: string;
  phoneE164: string | null;
  email: string;
}

function toDraft(o: WebcheckinOccupant): DraftOccupant {
  return {
    id: o.id,
    linkedReservationId: o.linkedReservationId,
    role: o.role,
    position: o.position,
    firstName: o.firstName || "",
    lastName: o.lastName || "",
    dateOfBirth: o.dateOfBirth || "",
    nationality: o.nationality || "",
    documentType: (o.documentType || "passport") as WebcheckinDocumentType,
    documentNumber: o.documentNumber || "",
    phoneE164: o.phoneE164 ?? null,
    email: o.email || "",
  };
}

export function CompanionsScreen({ token }: { token: string }) {
  const { state, dispatch } = useWizardContext();
  const payload = state.payload!;

  const [drafts, setDrafts] = useState<Record<string, DraftOccupant>>(() => {
    const out: Record<string, DraftOccupant> = {};
    for (const o of payload.session.occupants) out[o.id] = toDraft(o);
    return out;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const groups = payload.chain.rooms.map((r) => ({
    room: r,
    occupants: payload.session.occupants
      .filter((o) => o.linkedReservationId === r.linkedReservationId)
      .sort((a, b) => a.position - b.position),
  }));

  function patchDraft(id: string, p: Partial<DraftOccupant>) {
    setDrafts((d) => ({ ...d, [id]: { ...d[id]!, ...p } }));
  }

  function adultIsValid(d: DraftOccupant): boolean {
    if (!d.firstName.trim() || !d.lastName.trim()) return false;
    if (!d.dateOfBirth || !d.nationality) return false;
    if (!d.documentType || !d.documentNumber.trim()) return false;
    return true;
  }
  function childIsValid(d: DraftOccupant): boolean {
    return !!(d.firstName.trim() && d.lastName.trim() && d.dateOfBirth);
  }
  function rowIsValid(d: DraftOccupant): boolean {
    return d.role === "primary" || (d.role === "adult" ? adultIsValid(d) : childIsValid(d));
  }
  const allValid = Object.values(drafts).every(rowIsValid);

  async function handleContinue() {
    setBusy(true); setError(null);
    try {
      const tasks = Object.values(drafts)
        .filter((d) => d.role !== "primary")
        .map(async (d) => {
          await updateOccupant(token, d.id, {
            firstName: d.firstName.trim(),
            lastName: d.lastName.trim(),
            dateOfBirth: d.dateOfBirth || null,
            nationality: d.nationality || null,
            documentType: d.documentType,
            documentNumber: d.documentNumber.trim() || null,
            phoneE164: d.phoneE164,
            email: d.email.trim() || null,
          });
        });
      await Promise.all(tasks);
      dispatch({ type: "next" });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const hasCompanions = Object.values(drafts).some((d) => d.role !== "primary");
  const currentYear = new Date().getFullYear();

  return (
    <div>
      <h1 className="screen-h1">Companion details</h1>
      <p className="screen-p mt-2">
        {hasCompanions
          ? "Please add the details for everyone staying with you. Adults need the same identity + travel-document info as the main guest."
          : "No companions on this booking — tap Continue to proceed."}
      </p>

      <div className="mt-5 space-y-5">
        {groups.map((g, idx) => (
          <div key={g.room.linkedReservationId} className="rounded-xl border bg-card p-4 shadow-sm">
            <div className="mb-3">
              <div className="text-sm font-semibold">
                Room {idx + 1}{g.room.roomTypeName ? ` — ${g.room.roomTypeName}` : ""}
              </div>
              <div className="text-xs text-muted-foreground">
                {g.room.adults} adult{g.room.adults !== 1 ? "s" : ""}
                {g.room.children > 0 ? `, ${g.room.children} child${g.room.children !== 1 ? "ren" : ""}` : ""}
                {g.room.infants > 0 ? `, ${g.room.infants} infant${g.room.infants !== 1 ? "s" : ""}` : ""}
              </div>
            </div>

            <div className="space-y-4">
              {g.occupants.map((o) => {
                const d = drafts[o.id]!;
                if (o.role === "primary") {
                  return (
                    <div key={o.id} className="rounded-lg border bg-muted/30 p-3">
                      <RoleBadge role="primary" />
                      <div className="mt-1 font-medium">{d.firstName} {d.lastName}</div>
                      <div className="text-xs text-muted-foreground">Saved on the previous step.</div>
                    </div>
                  );
                }
                const isChild = o.role === "child" || o.role === "infant";
                return (
                  <div key={o.id} className="rounded-lg border p-4 space-y-4">
                    <RoleBadge role={o.role} position={o.position} />

                    <SectionHeading>Identity</SectionHeading>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Field label="First name *">
                        <Input value={d.firstName} onChange={(e) => patchDraft(o.id, { firstName: e.target.value })} />
                      </Field>
                      <Field label="Last name *">
                        <Input value={d.lastName} onChange={(e) => patchDraft(o.id, { lastName: e.target.value })} />
                      </Field>
                      <Field label="Date of birth *">
                        <DatePicker
                          value={d.dateOfBirth || null}
                          onChange={(v) => patchDraft(o.id, { dateOfBirth: v ?? "" })}
                          placeholder="Pick a date"
                          fromYear={1925}
                          toYear={currentYear}
                        />
                      </Field>
                      {!isChild && (
                        <Field label="Nationality *">
                          <CountrySelect value={d.nationality} onChange={(v) => patchDraft(o.id, { nationality: v })} />
                        </Field>
                      )}
                    </div>

                    {!isChild && (
                      <>
                        <SectionHeading>Travel document</SectionHeading>
                        {/* The type is stated, never photographed — see the
                            note on the same control in RegisterScreen. */}
                        <div className="grid grid-cols-1 gap-3 mb-3">
                          <Field label="Document type *">
                            <Select
                              value={d.documentType ?? "passport"}
                              onValueChange={(v) => patchDraft(o.id, { documentType: v as WebcheckinDocumentType })}
                            >
                              <SelectTrigger className="cursor-pointer">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="passport" className="cursor-pointer">Passport</SelectItem>
                                <SelectItem value="national_id" className="cursor-pointer">National ID</SelectItem>
                                <SelectItem value="driver_licence" className="cursor-pointer">Driver&apos;s licence</SelectItem>
                                <SelectItem value="other" className="cursor-pointer">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </Field>
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                          <Field label="Document number *">
                            <Input value={d.documentNumber} onChange={(e) => patchDraft(o.id, { documentNumber: e.target.value })} />
                          </Field>
                        </div>

                        <SectionHeading>Contact</SectionHeading>
                        <div className="grid grid-cols-1 gap-3">
                          <Field label="Email" hint="Optional. We use it only if we need to reach this guest.">
                            <Input
                              type="email"
                              autoComplete="email"
                              value={d.email}
                              onChange={(e) => patchDraft(o.id, { email: e.target.value })}
                            />
                          </Field>
                          <Field label="Phone number" hint="Optional. Useful for reception on arrival day.">
                            <PhoneInput
                              value={d.phoneE164}
                              onChange={(v) => patchDraft(o.id, { phoneE164: v })}
                              defaultDialIso2={d.nationality || "GR"}
                            />
                          </Field>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {error && <p className="mt-4 text-sm text-destructive">{error}</p>}

      <Button className="mt-6 w-full h-11" disabled={!allValid || busy} onClick={handleContinue}>
        {busy ? "Saving…" : "Continue"}
      </Button>
      {!allValid && (
        <p className="mt-3 text-xs text-amber-700">
          Every adult companion needs the same details as the main guest.
        </p>
      )}
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{children}</h3>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function CountrySelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <CountryPicker
      value={value}
      onChange={onChange}
      placeholder="— Select —"
      triggerClassName="w-full"
    />
  );
}

function RoleBadge({ role, position }: { role: WebcheckinOccupant["role"]; position?: number }) {
  const map: Record<WebcheckinOccupant["role"], { label: string; cls: string }> = {
    primary: { label: "Main guest", cls: "bg-primary/10 text-primary border-primary/30" },
    adult:   { label: `Adult ${position ?? ""}`.trim(), cls: "bg-amber-50 text-amber-800 border-amber-200" },
    child:   { label: `Child ${position ?? ""}`.trim(), cls: "bg-sky-50 text-sky-800 border-sky-200" },
    infant:  { label: `Infant ${position ?? ""}`.trim(), cls: "bg-purple-50 text-purple-800 border-purple-200" },
  };
  const cfg = map[role];
  return (
    <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider", cfg.cls)}>
      {cfg.label}
    </span>
  );
}
